import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Grid, Card, CardContent, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, LinearProgress } from '@mui/material';
import { TrendingUp, Warning, CheckCircle } from '@mui/icons-material';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface CustomerRetentionChurnProps {
  startDate: string;
  endDate: string;
}

const CustomerRetentionChurn: React.FC<CustomerRetentionChurnProps> = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    setTimeout(() => {
      setData({
        retention_rate: 87.5,
        churn_rate: 12.5,
        at_risk_customers: 24,
        total_customers: 320,
        churn_trend: Array.from({ length: 6 }, (_, i) => ({
          month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'][i],
          churn: 10 + Math.random() * 8,
          retention: 85 + Math.random() * 10
        })),
        at_risk_list: [
          { name: 'Acme Corp', score: 85, factors: ['Low engagement', 'No recent activity', 'CSAT declining'], last_interaction: 45 },
          { name: 'Tech Solutions', score: 72, factors: ['Multiple escalations', 'Long resolution times'], last_interaction: 30 },
          { name: 'Global Inc', score: 68, factors: ['Negative sentiment'], last_interaction: 25 }
        ]
      });
      setLoading(false);
    }, 500);
  }, [startDate, endDate]);

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  if (!data) return <Alert severity="info">No retention data available</Alert>;

  const trendChartOptions: ApexOptions = {
    chart: { type: 'line', toolbar: { show: false } },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    xaxis: { categories: data.churn_trend.map((d: any) => d.month) },
    yaxis: { min: 0, max: 100, labels: { formatter: (val) => `${val.toFixed(0)}%` } },
    colors: ['#4caf50', '#f44336'],
    legend: { position: 'top' }
  };

  return (
    <Box>
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}><CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircle color="success" />
              <Box>
                <Typography color="textSecondary" variant="body2">Retention Rate</Typography>
                <Typography variant="h4" color="success.main">{data.retention_rate}%</Typography>
              </Box>
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#ffebee' }}><CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <Warning color="error" />
              <Box>
                <Typography color="textSecondary" variant="body2">Churn Rate</Typography>
                <Typography variant="h4" color="error">{data.churn_rate}%</Typography>
              </Box>
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">At-Risk Customers</Typography>
            <Typography variant="h4" color="primary">{data.at_risk_customers}</Typography>
            <Chip label="High Priority" size="small" color="error" sx={{ mt: 1 }} />
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Total Customers</Typography>
            <Typography variant="h4" color="primary">{data.total_customers}</Typography>
            <Typography variant="caption">Active accounts</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Retention & Churn Trend (6 Months)</Typography>
        <Chart
          options={trendChartOptions}
          series={[
            { name: 'Retention Rate', data: data.churn_trend.map((d: any) => d.retention) },
            { name: 'Churn Rate', data: data.churn_trend.map((d: any) => d.churn) }
          ]}
          type="line"
          height={300}
        />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>At-Risk Customers (Churn Prediction)</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell align="center">Churn Risk Score</TableCell>
                <TableCell>Contributing Factors</TableCell>
                <TableCell align="right">Last Interaction</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.at_risk_list.map((customer: any, index: number) => (
                <TableRow key={index} hover sx={{ bgcolor: customer.score > 80 ? '#ffebee' : 'inherit' }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{customer.name}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box>
                      <Typography variant="body2" fontWeight="bold" color="error">{customer.score}%</Typography>
                      <LinearProgress variant="determinate" value={customer.score} color="error" sx={{ mt: 0.5, height: 6, borderRadius: 3 }} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {customer.factors.map((factor: string, idx: number) => (
                        <Chip key={idx} label={factor} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption">{customer.last_interaction} days ago</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="Contact Now" size="small" color="error" clickable />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Alert severity="warning" sx={{ mt: 3 }}>
        <Typography variant="subtitle2">⚠️ Churn Prevention Actions</Typography>
        <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
          <li><Typography variant="caption">Immediately reach out to {data.at_risk_list.length} high-risk customers</Typography></li>
          <li><Typography variant="caption">Review and resolve outstanding issues for at-risk accounts</Typography></li>
          <li><Typography variant="caption">Offer loyalty programs or special support for declining engagement customers</Typography></li>
          <li><Typography variant="caption">Schedule quarterly business reviews for high-value accounts</Typography></li>
        </ul>
      </Alert>

      <Paper sx={{ p: 3, mt: 3, bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" gutterBottom>🤖 Churn Prediction Model</Typography>
        <Typography variant="body2" paragraph>
          Our AI model analyzes 10+ factors to predict churn risk:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" display="block"><strong>• Engagement Metrics:</strong> Login frequency, feature usage, support interactions</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" display="block"><strong>• Satisfaction Indicators:</strong> CSAT scores, sentiment analysis, NPS</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" display="block"><strong>• Behavioral Patterns:</strong> Response delays, escalations, complaint frequency</Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default CustomerRetentionChurn;
